import { useTranslation } from "react-i18next";
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import ownerService from "../services/owner.service";
import { IOwnerObject } from "../services/api.types";

interface OwnerContextType {
  owner: IOwnerObject | null;
  loading: boolean;
  error: string | null;
  setError: (error: string | null) => void;
  refetch: () => Promise<void>;
}

const OwnerContext = createContext<OwnerContextType | undefined>(undefined);

// Background refresh cadence for the Owner detail page. Keeps worker
// availability chips live while the page is open without user action.
const OWNER_REFRESH_INTERVAL_MS = 20000;

export const useOwner = () => {
  const context = useContext(OwnerContext);
  if (context === undefined) {
    throw new Error("useOwner must be used within an OwnerProvider");
  }
  return context;
};

interface OwnerProviderProps {
  children: ReactNode;
}

export const OwnerProvider: React.FC<OwnerProviderProps> = ({ children }) => {
  const { t } = useTranslation();
  const { ownerID } = useParams<{ ownerID: string }>();
  const [owner, setOwner] = useState<IOwnerObject | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchOwnerData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (!ownerID) {
        throw new Error(t("owners.missingId"));
      }
      const response = await ownerService.getOwnerById(ownerID);
      setOwner(response);
    } catch (err: any) {
      setError(err.message || t("owners.fetchFailed"));
      setOwner(null);
    } finally {
      setLoading(false);
    }
  }, [ownerID, t]);

  useEffect(() => {
    fetchOwnerData();
  }, [fetchOwnerData]);

  // Silent background refresh: updates owner/employee data (incl. worker
  // availability) without touching loading/error state and without ever
  // blanking the current owner. Keeps the detail page live while mounted.
  const silentRefreshOwnerData = useCallback(async () => {
    if (!ownerID) {
      return;
    }
    try {
      const response = await ownerService.getOwnerById(ownerID);
      setOwner(response);
    } catch (err) {
      // Swallow transient failures (network blip, mid-refresh 401) and keep
      // the last good data — a background poll must never blank the page.
      console.warn("Owner background refresh failed; keeping last good data.", err);
    }
  }, [ownerID]);

  useEffect(() => {
    if (!ownerID) {
      return;
    }
    const intervalId = setInterval(
      silentRefreshOwnerData,
      OWNER_REFRESH_INTERVAL_MS
    );
    return () => clearInterval(intervalId);
  }, [ownerID, silentRefreshOwnerData]);

  const value: OwnerContextType = {
    owner,
    loading,
    error,
    setError,
    refetch: fetchOwnerData,
  };

  return (
    <OwnerContext.Provider value={value}>{children}</OwnerContext.Provider>
  );
};
